import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Settings, Eye, EyeOff, Copy, RefreshCw, Key } from '../ui/icons';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { tenantsApi } from '../../services/api';
import { Tenant } from '../../services/api';

const TenantSettings: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();
  const [tenantInfo, setTenantInfo] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiCredentials, setShowApiCredentials] = useState(false);

  useEffect(() => {
    if (permissions.tenantId) {
      loadTenantInfo();
    }
  }, [permissions.tenantId]);

  const loadTenantInfo = async () => {
    try {
      setLoading(true);
      if (permissions.tenantId) {
        const response = await tenantsApi.getById(permissions.tenantId);
        setTenantInfo(response.data);
      }
    } catch (error) {
      console.error('Error loading tenant info:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
    console.log(`${label} copiado al portapapeles`);
  };

  const maskText = (text: string, visible: boolean) => {
    if (visible) return text;
    return '*'.repeat(text.length);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Configuración</h1>
        <p className="text-muted-foreground">Gestiona la configuración de tu cuenta y empresa</p>
      </div>

      {/* Company Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Información de la Empresa</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Razón Social</Label>
              <Input
                id="company_name"
                value={tenantInfo?.company_name || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="ruc">RUC</Label>
              <Input
                id="ruc"
                value={tenantInfo?.ruc || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="email">Email de Contacto</Label>
              <Input
                id="email"
                value={tenantInfo?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="environment">Ambiente</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="environment"
                  value={tenantInfo?.environment === 'beta' ? 'Pruebas (Beta)' : 'Producción'}
                  disabled
                  className="bg-muted"
                />
                <Badge variant={tenantInfo?.environment === 'beta' ? 'secondary' : 'default'}>
                  {tenantInfo?.environment?.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>

          {tenantInfo?.razon_social_completa && (
            <div>
              <Label htmlFor="razon_completa">Razón Social Completa</Label>
              <Input
                id="razon_completa"
                value={tenantInfo.razon_social_completa}
                disabled
                className="bg-muted"
              />
            </div>
          )}

          {tenantInfo?.direccion_fiscal && (
            <div>
              <Label htmlFor="direccion">Dirección Fiscal</Label>
              <Input
                id="direccion"
                value={tenantInfo.direccion_fiscal}
                disabled
                className="bg-muted"
              />
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            <p>💡 Para modificar estos datos, contacta con el administrador del sistema.</p>
          </div>
        </CardContent>
      </Card>

      {/* SUNAT Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>Credenciales SUNAT (Clave SOL)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="clave_sol_usuario">Usuario Clave SOL</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="clave_sol_usuario"
                  value={tenantInfo?.clave_sol_usuario || 'No configurado'}
                  disabled
                  className="bg-muted"
                />
                {tenantInfo?.clave_sol_usuario && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(tenantInfo.clave_sol_usuario!, 'Usuario')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="clave_sol_password">Contraseña Clave SOL</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="clave_sol_password"
                  type={showPassword ? 'text' : 'password'}
                  value={tenantInfo?.clave_sol_password ? maskText('************', showPassword) : 'No configurado'}
                  disabled
                  className="bg-muted"
                />
                {tenantInfo?.clave_sol_password && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(tenantInfo.clave_sol_password!, 'Contraseña')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {tenantInfo?.allowed_document_types && (
            <div>
              <Label>Tipos de Documento Permitidos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {tenantInfo.allowed_document_types.map((type) => (
                  <Badge key={type} variant="outline">
                    {type === '01' ? 'Facturas' :
                     type === '03' ? 'Boletas' :
                     type === '07' ? 'Notas de Crédito' :
                     type === '08' ? 'Notas de Débito' : type}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            <p>🔒 Las credenciales SUNAT son gestionadas de forma segura por el administrador.</p>
          </div>
        </CardContent>
      </Card>

      {/* Detracción SPOT - Banco de la Nación */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>💳 Detracción SPOT - Banco de la Nación</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-purple-900">Sistema de Pago de Obligaciones Tributarias (SPOT)</h4>
            <p className="text-sm text-purple-800 mt-1">
              Cuenta del Banco de la Nación utilizada para el depósito de detracciones en facturas sujetas a este régimen.
            </p>
          </div>

          <div>
            <Label htmlFor="cuenta_banco_nacion">Cuenta del Banco de la Nación (11 dígitos)</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="cuenta_banco_nacion"
                value={tenantInfo?.cuenta_banco_nacion || 'No configurado'}
                disabled
                className="bg-muted"
              />
              {tenantInfo?.cuenta_banco_nacion ? (
                <>
                  <Badge variant="default" className="bg-green-600">
                    ✓ Configurada
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(tenantInfo.cuenta_banco_nacion!, 'Cuenta Banco Nación')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Badge variant="secondary">
                  No configurada
                </Badge>
              )}
            </div>
          </div>

          <div className="p-3 bg-purple-100 rounded-lg">
            <p className="text-xs text-purple-700 mb-2">
              <strong>ℹ️ Información importante:</strong>
            </p>
            <ul className="text-xs text-purple-600 space-y-1 ml-4 list-disc">
              <li>La cuenta debe tener exactamente <strong>11 dígitos</strong></li>
              <li>Es una cuenta del <strong>Banco de la Nación</strong> específica para detracciones</li>
              <li>Se auto-carga automáticamente al crear facturas con detracción</li>
              <li>Solo aplica para facturas con <strong>montos sujetos a detracción</strong> según el Catálogo 54 de SUNAT</li>
            </ul>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>🔒 La cuenta del Banco de la Nación es gestionada de forma segura por el administrador.</p>
          </div>
        </CardContent>
      </Card>

      {/* GRE OAuth 2.0 Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>🚚 Credenciales GRE - Guías de Remisión Electrónica (OAuth 2.0)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-900">API REST SUNAT para Guías de Remisión</h4>
            <p className="text-sm text-green-800 mt-1">
              Estas credenciales son necesarias para emitir Guías de Remisión Electrónicas mediante el sistema GRE de SUNAT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gre_client_id">Client ID (API SUNAT)</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="gre_client_id"
                  value={tenantInfo?.gre_client_id || 'No configurado'}
                  disabled
                  className="bg-muted"
                />
                {tenantInfo?.gre_client_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(tenantInfo.gre_client_id!, 'Client ID')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="gre_client_secret">Client Secret (Clave API)</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="gre_client_secret"
                  type={showPassword ? 'text' : 'password'}
                  value={tenantInfo?.gre_client_secret ? maskText('************', showPassword) : 'No configurado'}
                  disabled
                  className="bg-muted"
                />
                {tenantInfo?.gre_client_secret && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(tenantInfo.gre_client_secret!, 'Client Secret')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="gre_email_autorizado">Email Autorizado (para notificaciones GRE)</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="gre_email_autorizado"
                type="email"
                value={tenantInfo?.gre_email_autorizado || 'No configurado'}
                disabled
                className="bg-muted"
              />
              {tenantInfo?.gre_email_autorizado && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(tenantInfo.gre_email_autorizado!, 'Email Autorizado')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="p-3 bg-green-100 rounded-lg">
            <p className="text-xs text-green-700 mb-2">
              <strong>ℹ️ Nota importante:</strong> Las credenciales Usuario SOL y Contraseña SOL son las mismas que configuraste arriba (para Facturas/Boletas).
            </p>
            <ul className="text-xs text-green-600 space-y-1 ml-4 list-disc">
              <li><strong>Client ID:</strong> UUID proporcionado por SUNAT para acceso OAuth 2.0</li>
              <li><strong>Client Secret:</strong> Clave secreta en formato base64 para OAuth</li>
              <li><strong>Email Autorizado:</strong> Email para recibir notificaciones de GRE</li>
              <li><strong>Usuario/Contraseña SOL:</strong> Se reutilizan las credenciales configuradas arriba</li>
            </ul>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>🔒 Las credenciales GRE son gestionadas de forma segura por el administrador.</p>
          </div>
        </CardContent>
      </Card>

      {/* API Access Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Key className="h-5 w-5" />
            <span>Credenciales de API</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900">Acceso Programático al API</h4>
            <p className="text-sm text-blue-800 mt-1">
              Utiliza estas credenciales para integrar tu sistema ERP con nuestra plataforma.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="api_username">Usuario API</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="api_username"
                  value={user?.username || 'No disponible'}
                  disabled
                  className="bg-muted"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(user?.username || '', 'Usuario API')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="api_password">Contraseña API</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="api_password"
                  type={showApiCredentials ? 'text' : 'password'}
                  value={showApiCredentials ? 'Contacta al administrador para obtener la contraseña' : '********************'}
                  disabled
                  className="bg-muted"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowApiCredentials(!showApiCredentials)}
                >
                  {showApiCredentials ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="api_gateway">URL del API Gateway</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="api_gateway"
                  value="http://localhost:8015"
                  disabled
                  className="bg-muted"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard('http://localhost:8015', 'URL del API')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <h4 className="font-medium">Instrucciones de Uso</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <strong>1. Autenticación:</strong>
                <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                  POST http://localhost:8015/auth/login<br/>
                  {JSON.stringify({ username: user?.username, password: "tu_contraseña" }, null, 2)}
                </code>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>2. Usar Token:</strong>
                <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                  Authorization: Bearer &lt;access_token&gt;
                </code>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>3. Crear Documento:</strong>
                <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                  POST http://localhost:8015/documents
                </code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Mi Perfil de Usuario</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="username">Nombre de Usuario</Label>
              <Input
                id="username"
                value={user?.username || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="user_email">Email</Label>
              <Input
                id="user_email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="full_name">Nombre Completo</Label>
              <Input
                id="full_name"
                value={user?.full_name || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="role">Rol</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="role"
                  value="Usuario del Tenant"
                  disabled
                  className="bg-muted"
                />
                <Badge variant="outline">{user?.role}</Badge>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Cambiar Contraseña</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Para cambiar tu contraseña, contacta con el administrador del sistema.
            </p>
            <Button variant="outline" disabled>
              <RefreshCw className="h-4 w-4 mr-2" />
              Solicitar Cambio de Contraseña
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantSettings;